import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RateCardPresetService } from '../../services/rate-card-presets/rate-card-preset.service';
import { Message } from '../../classes/Message';
import { MessageService } from 'primeng';
import { RateCardPresetDTO } from '../../interfaces/rate-card-presets/rate-card-preset.dto.interface';
import { CreateRateCardPresetDTO } from '../../interfaces/rate-card-presets/create-rate-card-preset.dto.interface';
import { RateCardCellDataEvent } from '../../interfaces/rate-card-presets/rate-card-cell-data-event.interface';
import { compare, deepClone } from 'fast-json-patch';
import { AgreementService, UserService } from '../../services';
import { IRateCardInclusivityType } from '../../interfaces/ratecard-inclusivity-type';

@Component({
  templateUrl: './preset-ratecard-admin.component.html',
  styleUrls: ['./preset-ratecard-admin.component.scss'],
})
export class PresetRateCardAdminComponent implements OnInit {
  constructor(
    private fb: FormBuilder,
    private presetService: RateCardPresetService,
    private messageService: MessageService,
    private agreementService: AgreementService,
    private userService: UserService,
  ) {}

  form: FormGroup;
  isBusy = true;

  presets: RateCardPresetDTO[];
  selectedPreset: RateCardPresetDTO;
  selectedRateCardData: any;

  inclusivityTypes: IRateCardInclusivityType[] = [];

  isPricingAdmin: boolean;

  async ngOnInit() {
    this.isPricingAdmin = await this.userService.isUserInRole('PricingAdmin');

    this.form = this.fb.group({
      presetName: ['', Validators.required],
    });

    this.loadRateCardInclusivityTypes();
    this.loadPresets();
  }

  loadPresets() {
    this.isBusy = true;
    this.presetService.getPresets().then((res) => {
      this.presets = res;
      this.isBusy = false;
    });
  }

  get f() {
    return this.form.controls;
  }

  createPreset() {
    if (this.form.invalid || !this.isPricingAdmin) return;

    this.isBusy = true;

    const dto = <CreateRateCardPresetDTO>{
      presetName: this.f.presetName.value,
    };

    this.presetService
      .createPreset(dto)
      .then((res) => {
        if (res) {
          this.presets.push(res);

          const ratecards = deepClone(res.servicePricings);
          this.selectedRateCardData = this.presetService.getRateCardData(ratecards);
        } else {
          this.messageService.add(
            Message.Error('Could not create preset, ensure the name is unique'),
          );
        }
        this.isBusy = false;
      })
      .catch((err) => {
        this.messageService.add(Message.Error('An error prevented the preset being created'));
        this.isBusy = false;
      });
  }

  loadSelectedPreset() {
    if (!this.selectedPreset) return;

    // load the full data for the selected preset if it is just the shell
    if (this.selectedPreset.servicePricings?.length == 0) {
      this.isBusy = true;
      this.presetService.getPreset(this.selectedPreset.id).then((res) => {
        this.selectedPreset.servicePricings = res.servicePricings;
        //console.log('PRESET: ', this.selectedPreset);

        const ratecards = deepClone(this.selectedPreset.servicePricings);
        this.selectedRateCardData = this.presetService.getRateCardData(ratecards);

        this.isBusy = false;
      });
    } else {
      const ratecards = deepClone(this.selectedPreset.servicePricings);
      this.selectedRateCardData = this.presetService.getRateCardData(ratecards);
    }
  }

  rateCardDataChanged(event: RateCardCellDataEvent, selectedRateCardDatumElement: any) {
    //console.log("Rate card value changed: ", event, selectedRateCardDatumElement[event.rowIndex]);

    const spId = selectedRateCardDatumElement[event.rowIndex].id;
    const pre = selectedRateCardDatumElement[event.rowIndex];
    let sp = this.selectedPreset.servicePricings.find((x) => x.id == spId);

    if (sp) {
      //console.log("Found preset pricing A: ", pre);
      //console.log("Found preset pricing B: ", sp);

      // patch value
      let patch = compare(sp.pricing, pre.pricing);

      // slight hack: delete any 'undefined' operations (not sure why it's happening)
      patch = patch.filter((x) => x.path != '/undefined');

      //console.log('PATCH: ', patch);
      this.presetService.patchPricing(sp.pricingId, patch).then(() => {
        // update preset now it's been patched
        sp.pricing = deepClone(pre.pricing);
      });
    }
  }

  activationChange(item: any) {
    let pre = deepClone(item);
    if (!item.inactiveDate) {
      item.inactiveDate = new Date();
    } else {
      item.inactiveDate = null;
    }

    let patch = compare(pre, item);
    //console.log("PATCH: ", patch)

    this.presetService.patchPreset(item.id, patch).then(() => {});
  }

  loadRateCardInclusivityTypes(): void {
    this.agreementService.getInclusivityTypes().then((result) => {
      this.inclusivityTypes = result;
    });
  }
}
